import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGIN } from "@/constants/ui";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Anmeldung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 ph-stripe flex items-center justify-center px-6 py-12 relative overflow-hidden">
      {/* decorative accents */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-kauz-navy/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-kauz-gold/15 blur-3xl" />

      <div className="w-full max-w-sm relative">
        <div className="flex flex-col items-center gap-4 mb-8">
          <img
            src="/brand/kauz-icon.png"
            alt="Kauz Logistik"
            className="h-24 w-auto select-none"
            draggable="false"
          />
          <div className="text-center">
            <span data-testid="brand-name" className="font-display text-2xl font-semibold tracking-tight text-kauz-navy">
              Kauz Logistik e.K.
            </span>
            <div className="mt-1 h-0.5 w-16 mx-auto bg-kauz-gold" />
          </div>
        </div>

        <div className="ph-card p-6 md:p-8 bg-white shadow-xl border-slate-100 rounded-2xl">
          <h1 className="font-display text-lg font-semibold tracking-tight text-slate-900 text-center">
            Mitarbeiter-Login
          </h1>

          <form onSubmit={onSubmit} className="space-y-4 mt-6">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                E-Mail oder Vorname
              </Label>
              <Input
                id="email"
                type="text"
                required
                autoComplete="username"
                data-testid={LOGIN.email}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="z. B. Eugen oder anna@kauzwerk.de"
                className="h-11 focus-visible:ring-kauz-navy/40"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                Passwort
              </Label>
              <Input
                id="password"
                type="password"
                required
                data-testid={LOGIN.password}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 focus-visible:ring-kauz-navy/40"
              />
            </div>
            {error && (
              <div data-testid={LOGIN.error} className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid={LOGIN.submit}
              disabled={loading}
              className="w-full h-11 bg-kauz-navy hover:bg-kauz-navy-dark text-white font-medium shadow-sm hover:shadow transition-all"
            >
              {loading ? "Anmelden…" : "Anmelden"}
            </Button>
          </form>
        </div>

        <p className="text-[11px] text-slate-400 text-center mt-6">
          © {new Date().getFullYear()} Kauz Logistik e.K.
        </p>
      </div>
    </div>
  );
}
